import axios from "axios";
import authHeader from "./auth-header";
import { API_URL } from "../config";
import { compressImageFile } from "../utils/compressImage";

class ApiService {

  getMyEvents() {
    return axios.get(`${API_URL}/users/me/events`, { headers: authHeader() });
  }

  sendEventInvitations(eventId, userIds) {
    return axios.post(`${API_URL}/events/${eventId}/invitations`, { user_ids: userIds }, { headers: authHeader() });
  }

  redeemEventAccess(slug, token) {
    return axios.post(`${API_URL}/applications/${encodeURIComponent(slug)}/event-access`, { token });
  }

  getPublicEventRegistration(slug, uid) {
    return axios.get(`${API_URL}/applications/${encodeURIComponent(slug)}/events/${encodeURIComponent(uid)}/registration`);
  }

  getEventRegistrationLink(id) {
    return axios.get(`${API_URL}/events/${id}/registration-link`, { headers: authHeader() });
  }

  getEventRegistration(id) {
    return axios.get(`${API_URL}/events/${id}/registration`, { headers: authHeader() });
  }

  registerForEvent(id) {
    return axios.post(`${API_URL}/events/${id}/registration`, {}, { headers: authHeader() });
  }

  async getPlatformContext() {
    return axios.get(`${API_URL}/platform/context`);
  }

  async createUser_guest({
    username,
    password,
    firstname,
    lastname,
    captchaKey,
    applicationSlug = null,
    customFields = {},
    eventUid = null,
  }) {
    const params = {
        "username": username,
        "password": password,
        "captcha_key": captchaKey,
        "custom_fields": customFields,
        "croas": customFields.croas,
        "codice_fiscale": customFields.codice_fiscale,
        "provincia": customFields.provincia,
    }
    if (applicationSlug) params.application_slug = applicationSlug;
    if (eventUid) params.event_uid = eventUid;
    if (firstname !== "") params["first_name"] = firstname;
    if (lastname !== "") params["last_name"] = lastname;
    return await axios.post(API_URL+'/register', params, { headers: authHeader() });
  }

  async createUser_Admin(
    username,
    password,
    firstname,
    lastname,
    codiceFiscale,
    platformRole,
    applicationId = null
  ) {
    const params = {
      "username": username,
      "password": password,
      "codice_fiscale": codiceFiscale,
      "platform_role": platformRole,
      "application_id": applicationId,
      "is_admin": platformRole === "admin"
    };
    if (firstname !== "") params["first_name"] = firstname;
    if (lastname !== "") params["last_name"] = lastname;
    return await axios.post(API_URL + '/newuser', params, { headers: authHeader() });
  }

  async editUser(
    username,
    password = null,
    firstname,
    lastname,
    croas,
    codiceFiscale,
    provincia,
    id,
    isVerified,
    platformRole = null,
    applicationId = undefined
  ) {
    const currentUser = await this.getUserById(id);
    const isAdmin = currentUser.data.is_admin;

    const params = {
        "username": username,
        "is_admin": isAdmin,
        "croas": croas,
        "codice_fiscale": codiceFiscale,
        "provincia": provincia,
    }
    if (isVerified !== undefined) params["is_verified"] = isVerified;
    if (platformRole) params["platform_role"] = platformRole;
    if (applicationId !== undefined) params.application_id = applicationId;
    if (password !== "" && password != null) params['password'] = password
    if (firstname !== "") params["first_name"] = firstname;
    if (lastname !== "") params["last_name"] = lastname;
    return await axios.put(API_URL+'/users/'+id, params, { headers: authHeader() });
  }

  async setUserRole(userId, role) {
    return axios.post(`${API_URL}/users/${userId}/role`, role, { headers: authHeader() });
  }

  async getUserRole(userId){
    return axios.get(`${API_URL}/users/${userId}/role`, {headers: authHeader() });
  }

  async getUsers(verified = undefined){
    const params = {};
    if (verified === true) params.verified = true;
    if (verified === false) params.verified = false;
    return axios.get(API_URL+'/users', { headers: authHeader(), params });
  }

  async deleteUser(id){
    return axios.delete(API_URL+'/users/'+id, { headers: authHeader() });
  }

  async createEvent(date, name, description, embedLink, coverUrl = null, applicationId = null) {
    const params = {
      date,
      name,
      description,
      embed_link: embedLink,
      cover_url: coverUrl,
      finished: false,
    };
    if (applicationId) params.application_id = applicationId;
    return axios.post(`${API_URL}/newevent`, params, { headers: authHeader() });
  }

  async modifyEvent(date, name, description, embedLink, finished, id, coverUrl = undefined) {
    const params = {
      date,
      name,
      description,
      embed_link: embedLink,
      finished,
    };
    if (coverUrl !== undefined) params.cover_url = coverUrl;
    return axios.put(`${API_URL}/modifyevent/${id}`, params, { headers: authHeader() });
  }

  async deleteEvent(eventId, confirmationId) {
    return axios.delete(`${API_URL}/events/${eventId}`, {
      headers: authHeader(),
      data: { id: Number(confirmationId) },
    });
  }

  async uploadEventCover(eventId, file) {
    const compressed = await compressImageFile(file, {
      maxWidth: 1920,
      maxHeight: 1080,
      maxBytes: 850 * 1024,
      quality: 0.8,
    });
    const formData = new FormData();
    formData.append("file", compressed);
    return axios.post(`${API_URL}/events/${eventId}/cover`, formData, { headers: authHeader() });
  }

  async getApplications() {
    return axios.get(`${API_URL}/applications`, { headers: authHeader() });
  }

  async getMyApplication() {
    return axios.get(`${API_URL}/applications/mine`, { headers: authHeader() });
  }

  async createApplication(payload) {
    return axios.post(`${API_URL}/applications`, payload, { headers: authHeader() });
  }

  async getApplicationBySlug(slug) {
    return axios.get(`${API_URL}/applications/by-slug/${slug}`);
  }

  async getFeaturedEventBySlug(slug) {
    return axios.get(`${API_URL}/applications/by-slug/${slug}/featured-event`);
  }

  async updateApplication(applicationId, payload) {
    return axios.put(`${API_URL}/applications/${applicationId}`, payload, { headers: authHeader() });
  }

  async deleteApplication(applicationId) {
    return axios.delete(`${API_URL}/applications/${applicationId}`, { headers: authHeader() });
  }

  async uploadApplicationLogo(applicationId, file) {
    const compressed = await compressImageFile(file, {
      maxWidth: 1200,
      maxHeight: 1200,
      maxBytes: 700 * 1024,
      quality: 0.85,
    });
    const formData = new FormData();
    formData.append("file", compressed);
    return axios.post(`${API_URL}/applications/${applicationId}/logo`, formData, {
      headers: authHeader(),
    });
  }

  async getIscrizioni() {
    return axios.get(`${API_URL}/iscrizioni`, { headers: authHeader() });
  }

  async getGestori() {
    return axios.get(`${API_URL}/gestori`, { headers: authHeader() });
  }

  async createGestore(payload) {
    return axios.post(`${API_URL}/gestori`, payload, { headers: authHeader() });
  }

  async assignGestore(userId, applicationId) {
    return axios.put(`${API_URL}/gestori/${userId}`, { application_id: applicationId }, { headers: authHeader() });
  }

  async updateGestore(userId, payload) {
    return axios.put(`${API_URL}/gestori/${userId}`, payload, { headers: authHeader() });
  }

  async deleteGestore(userId) {
    return axios.delete(`${API_URL}/gestori/${userId}`, { headers: authHeader() });
  }

  async downloadEventAttendancePDF(eventId, extra) {
    return this.downloadEventAttendance(eventId, 'pdf');
  }

  async downloadEventAttendance(eventId, format) {
    const response = await axios.get(`${API_URL}/events/${eventId}/attendance/export/${format}`, {
      headers: authHeader(),
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const extension = format === 'xls' || format === 'xlsx' ? 'xlsx' : format;
    link.setAttribute('download', `presenze_evento_${eventId}.${extension}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async setEventStatus(eventId, status) {
    return axios.post(`${API_URL}/events/${eventId}/status`, { eventStatus: status }, { headers: authHeader() });
  }

  async startEvent(eventId) {
    return axios.post(`${API_URL}/events/${eventId}/start`, {}, { headers: authHeader() });
  }

  async endEvent(eventId) {
    return axios.post(`${API_URL}/events/${eventId}/end`, {}, { headers: authHeader() });
  }

  async getEventAttendance(eventId) {
    return axios.get(`${API_URL}/events/${eventId}/attendance`, { headers: authHeader() });
  }

  async getEvent(id){
    return axios.get(API_URL+'/events/'+id, {headers: authHeader() });
  }

  async getEvents(){
    return axios.get(API_URL+'/events', {headers: authHeader() });
  }

  async getEventStatus(id){
    return axios.get(API_URL+'/events/'+id+'/status', {headers: authHeader() });
  }

  async getEventEmbed(id){
    return axios.get(API_URL+'/events/'+id+'/embed', {headers: authHeader() });
  }

  async sendAttendance(id){
    return axios.get(API_URL+'/attendance/'+id, {headers: authHeader() });
  }

  async getUserById(id){
    return axios.get(API_URL+'/users/'+id, { headers: authHeader() });
  }

  async getCurrentUser() {
    return axios.get(API_URL + '/users/me', { headers: authHeader() });
  }

  async updateMyProfile(payload) {
    return axios.put(API_URL + '/users/me', payload, { headers: authHeader() });
  }

  async getStats(){
    return axios.get(API_URL+'/userstats', { headers: authHeader() });
  }

  async getPlatformSettings() {
    return axios.get(API_URL + '/platform/settings');
  }

  async updatePlatformSettings(payload) {
    return axios.put(API_URL + '/platform/settings', payload, { headers: authHeader() });
  }

  async uploadPlatformLogo(file) {
    const compressed = await compressImageFile(file, {
      maxWidth: 1200,
      maxHeight: 1200,
      maxBytes: 700 * 1024,
      quality: 0.85,
    });
    const formData = new FormData();
    formData.append('file', compressed);
    return axios.post(API_URL + '/platform/settings/logo', formData, {
      headers: authHeader(),
    });
  }

  async uploadPlatformBanner(file) {
    const compressed = await compressImageFile(file, {
      maxWidth: 1920,
      maxHeight: 1080,
      maxBytes: 850 * 1024,
      quality: 0.8,
    });
    const formData = new FormData();
    formData.append('file', compressed);
    return axios.post(API_URL + '/platform/settings/banner', formData, {
      headers: authHeader(),
    });
  }

  async getPolls() {
    return axios.get(API_URL + '/polls', { headers: authHeader() });
  }

  async createPoll(payload) {
    return axios.post(API_URL + '/polls', payload, { headers: authHeader() });
  }

  async activatePoll(pollId) {
    return axios.post(`${API_URL}/polls/${pollId}/activate`, {}, { headers: authHeader() });
  }

  async closePoll(pollId) {
    return axios.post(`${API_URL}/polls/${pollId}/close`, {}, { headers: authHeader() });
  }

  async getPollResults(pollId) {
    return axios.get(`${API_URL}/polls/${pollId}/results`, { headers: authHeader() });
  }

  async downloadPollResults(pollId, format) {
    const response = await axios.get(`${API_URL}/polls/${pollId}/export/${format}`, {
      headers: authHeader(),
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    const extension = format === 'xls' || format === 'xlsx' ? 'xlsx' : format;
    link.setAttribute('download', `sondaggio_${pollId}_risultati.${extension}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async getActivePoll(eventId) {
    return axios.get(`${API_URL}/polls/active/${eventId}`, { headers: authHeader() });
  }

  async respondPoll(pollId, optionId) {
    return axios.post(`${API_URL}/polls/${pollId}/respond`, { option_id: optionId }, { headers: authHeader() });
  }

  async getMyEventChat(eventId) {
    return axios.get(`${API_URL}/events/${eventId}/chat`, { headers: authHeader() });
  }

  async sendEventChatMessage(eventId, body) {
    return axios.post(`${API_URL}/events/${eventId}/chat/messages`, { body }, { headers: authHeader() });
  }

  async getEventChats() {
    return axios.get(`${API_URL}/event-chats`, { headers: authHeader() });
  }

  async getEventChat(chatId) {
    return axios.get(`${API_URL}/event-chats/${chatId}`, { headers: authHeader() });
  }

  async replyEventChat(chatId, body) {
    return axios.post(`${API_URL}/event-chats/${chatId}/messages`, { body }, { headers: authHeader() });
  }

  async closeEventChat(chatId) {
    return axios.post(`${API_URL}/event-chats/${chatId}/close`, {}, { headers: authHeader() });
  }

  async verifyUser(id, key, applicationSlug){
    return axios.get(`${API_URL}/validate`, { params: { id, key, ...(applicationSlug ? { application_slug: applicationSlug } : {}) } });
  }

  async verifyUserEmail(userId) {
    return await axios.put(API_URL + `/users/${userId}/verify`, {}, { headers: authHeader() });
  }

  async verifyPasswordReset(key, applicationSlug){
    return axios.get(`${API_URL}/resetpassword`, { params: { verify_key: key, ...(applicationSlug ? { application_slug: applicationSlug } : {}) } });
  }

  async resetPassword(id, key, password, applicationSlug){
    var data = {
      "verify_key": key,
      "password": password
    }
    return axios.post(`${API_URL}/resetpassword`, data, { params: applicationSlug ? { application_slug: applicationSlug } : {} });
  }

  async resetRequest(username, applicationSlug){
    return axios.post(`${API_URL}/resetrequest`, null, { params: { username, ...(applicationSlug ? { application_slug: applicationSlug } : {}) } });
  }
}

export default new ApiService();
